import {
  ColumnData,
  OrderTypeProps,
  ProductsProps,
  ProductTypeFileds,
} from '../interface/interface';
import { Table } from './Table';
import Select, { components, ValueContainerProps } from 'react-select';
import {
  productStatusList,
  RemovedProductsCategory,
} from '../constant/constant';
import { Link } from 'react-router-dom';
import { useMainRef } from '../context/pageScrollContext';
import { useEffect } from 'react';
import formatePriceByCost from '../utils/formatPriceByCost';

export default function Products({
  loading,
  dataProps,
  searchProps,
  filterProps,
  sortProps,
  paginationProps,
  type,
}: Readonly<ProductsProps>) {
  const mainRef = useMainRef();

  useEffect(() => {
    if (!mainRef?.current) return;

    const savedPosition = localStorage.getItem('product');
    if (savedPosition) {
      if (mainRef.current) {
        setTimeout(() => {
          mainRef.current!.scrollTo({
            top: Number(savedPosition),
            behavior: 'smooth',
          });
        }, 1000);
      }
    }
  }, [mainRef]);

  const ValueContainer = ({ children, ...props }: ValueContainerProps<any>) => {
    let [values, input] = children as any;

    if (Array.isArray(values)) {
      const plural = values.length === 1 ? '' : 's';
      values = `${values.length} item${plural} selected`;
    }

    return (
      <components.ValueContainer {...props}>
        {values}
        {input}
      </components.ValueContainer>
    );
  };

  const columns: Array<ColumnData> = [
    {
      key: 'name',
      header: () => 'Name',
      isSortable: true,
      cell: (info) => (
        <Link
          to={`/product/${info?.row?.original?.product_code}`}
          state={{ type, from: window.location.pathname }}
          className="underline"
        >
          {info.getValue()}
        </Link>
      ),
    },
    {
      key: 'product_brand',
      header: () => 'Brand',
      isSortable: true,
      cell: (info) => info.getValue(),
    },
    {
      key: 'dayornight',
      header: () => 'Day / Night',
      isSortable: true,
      cell: (info) => info.getValue() ?? '-',
    },
    {
      key: 'product_strength',
      header: () => {
        return (
          <div>
            <p>Strength</p>
            <p>(CBD:THC)</p>
          </div>
        );
      },
      isSortable: true,
      cell: (info) => info.getValue() ?? '-',
    },
    {
      key: 'category',
      header: () => 'Category',
      isSortable: true,
      cell: (info) => info.getValue() ?? '-',
    },

    {
      key: 'formulation',
      header: () => 'Form',
      isSortable: true,
      cell: (info) => info.getValue() ?? '-',
    },
    {
      key: 'tga',
      header: () => 'TGA',
      isSortable: true,
      cell: (info) => info.getValue() ?? '-',
    },
    {
      key: 'inventory_quantity',
      header: () => 'Stock on Hand',
      isSortable: true,
      cell: (info) => {
        const qty = info.row.original.inventory_quantity;
        return <p>{qty > 0 ? qty : 'out-of-stock'}</p>;
      },
    },
    {
      key: 'price',
      header: () => 'Price',
      isSortable: true,
      cell: (info) => `$${info.getValue()}`,
    },
    {
      key: 'cost',
      header: () => 'Cost per',
      isSortable: true,
      cell: (info) => {
        const price = info?.row?.original?.price;
        const unit = info?.row?.original?.unit;
        const weight = info?.row?.original?.weight;

        return formatePriceByCost(price, unit, weight);
      },
    },
    {
      key: 'eta',
      header: () => 'Expiry / ETA',
      isSortable: true,
      cell: (info) => info.getValue() ?? '-',
    },
  ];

  return (
    <>
      <div className="flex items-center mb-5">
        <small className="ml-5">Status</small>
        <Select
          className="w-48  cursor-pointer ml-2 "
          value={filterProps.filter}
          options={productStatusList}
          onChange={(selected) => filterProps.setFilter(selected)}
          isSearchable={false}
        />

        {type !== ProductTypeFileds.BUNDLES ? (
          <>
            <small className="ml-5">Category</small>
            <Select
              classNames={{
                control: (state) =>
                  `${
                    state.isFocused
                      ? 'border-grey-400 border text-sm'
                      : 'border-grey-400 border text-sm'
                  } w-60 cursor-pointer ml-2`,
              }}
              value={
                localStorage.getItem('productCategroy')
                  ? JSON.parse(
                      localStorage.getItem('productCategroy') as string,
                    )
                  : sortProps.sortByCategory
              }
              options={dataProps.productCategory
                ?.filter(
                  (product) => !RemovedProductsCategory.includes(product.value),
                )
                ?.sort((a, b) => a.label.localeCompare(b.label))}
              onChange={(selected) => {
                sortProps.setSortByCategory([...selected]);
                localStorage.setItem(
                  'productCategroy',
                  JSON.stringify(selected),
                );
              }}
              isSearchable
              hideSelectedOptions={false}
              isMulti
              isClearable
              components={{ ValueContainer }}
            />
            <small className="ml-5">Formulation</small>
            <Select
              classNames={{
                control: (state) =>
                  `${
                    state.isFocused
                      ? 'border-grey-400 border text-sm'
                      : 'border-grey-400 border text-sm'
                  } w-60 cursor-pointer ml-2`,
              }}
              value={
                localStorage.getItem('productFormulation')
                  ? JSON.parse(
                      localStorage.getItem('productFormulation') as string,
                    )
                  : sortProps.sortByFormulation
              }
              options={dataProps.productFormulation?.sort((a, b) =>
                a.label.localeCompare(b.label),
              )}
              onChange={(selected) => {
                sortProps.setSortByFormulation([...selected]);
                localStorage.setItem(
                  'productFormulation',
                  JSON.stringify(selected),
                );
              }}
              isSearchable
              hideSelectedOptions={false}
              isMulti
              isClearable
              components={{ ValueContainer }}
            />
          </>
        ) : (
          <></>
        )}

        {type === ProductTypeFileds.SCRIPT_RANGE ? (
          <>
            <small className="ml-5">Condition</small>
            <Select
              classNames={{
                control: (state) =>
                  `${
                    state.isFocused
                      ? 'border-grey-400 border text-sm'
                      : 'border-grey-400 border text-sm'
                  } w-60 cursor-pointer ml-2`,
              }}
              value={
                localStorage.getItem('productCondition')
                  ? JSON.parse(
                      localStorage.getItem('productCondition') as string,
                    )
                  : sortProps.sortByCondition
              }
              options={dataProps.productCondition?.sort((a, b) =>
                a.label.localeCompare(b.label),
              )}
              onChange={(selected) => {
                sortProps.setSortByCondition([...selected]);
                localStorage.setItem(
                  'productCondition',
                  JSON.stringify(selected),
                );
              }}
              isSearchable
              hideSelectedOptions={false}
              isMulti
              isClearable
              components={{ ValueContainer }}
            />
          </>
        ) : (
          <></>
        )}
      </div>
      <Table
        columns={columns}
        data={dataProps.data}
        enablePagination={true}
        enableSorting={true}
        enableSearch={true}
        enableMultiSort={true}
        pagintation={paginationProps}
        search={searchProps}
        childLoader={loading}
        order={OrderTypeProps.PRODUCT}
      />
    </>
  );
}
